// Options payantes on the order detail page (Phase 4, 2026-08-01).
//
// An add-on is extra work in the atelier, and until now the only trace of one on
// this page was a small amber pill on the PDF card — easy to scroll past. This
// card sits ABOVE the numbered pipeline so it is the first thing seen when an
// order is opened, and it renders NOTHING at all for ordinary orders, so the
// common case is completely unchanged.
//
// Driven by order_items (migration 070), not by the relief_3d boolean: a second
// upsell added in Shopify appears here on its own, with no code change.

import type { Order } from '@/lib/api';
import { useT } from '@/lib/i18n';

/** Plain-French production notes, keyed by Shopify product gid. Anything not
 *  listed simply shows no note — an unknown add-on must still be visible. */
const PRODUCTION_NOTE: Record<string, string> = {
  'gid://shopify/Product/10891904057655':
    'Vernis sélectif sur le texte et les icônes de la couverture.',
};

export function OrderUpsellsCard({ order }: { order: Order }) {
  const { t } = useT();
  const upsells = (order.items ?? []).filter((i) => i.kind === 'upsell');
  if (upsells.length === 0) return null;

  return (
    <div className="rounded-xl border-2 bg-amber-50/60 border-amber-200 overflow-hidden">
      <div className="px-5 py-3 border-b border-amber-200/70 flex items-center gap-2">
        <span aria-hidden="true" className="text-amber-700">✦</span>
        <h3 className="font-semibold text-amber-900">
          {t('Options payantes — travail supplémentaire')}
        </h3>
      </div>

      <ul className="divide-y divide-amber-200/50">
        {upsells.map((item, idx) => {
          const note = item.shopify_product_gid ? PRODUCTION_NOTE[item.shopify_product_gid] : null;
          return (
            <li key={`${item.shopify_variant_gid ?? 'upsell'}-${idx}`} className="flex items-center gap-3 px-5 py-3">
              <div className="w-14 h-14 rounded-lg bg-white/70 shrink-0 overflow-hidden grid place-items-center ring-1 ring-amber-200" data-adm-media={item.image_url ? '' : undefined}>
                {item.image_url
                  ? <img src={item.image_url} alt="" loading="lazy" className="w-full h-full object-cover" />
                  : <span aria-hidden="true" className="text-amber-300 text-xl">✦</span>}
              </div>

              <div className="min-w-0 flex-1">
                <p className="font-medium text-amber-950 truncate">
                  {item.title ?? t('Option payante')}
                </p>
                <p className="text-sm text-amber-800">
                  {item.variant_title && <span>{item.variant_title} · </span>}
                  {/* Quantity is the album count — the add-on is bought per album. */}
                  {t('Quantité')} : <strong>{item.quantity}</strong>
                </p>
                {note && <p className="text-xs text-amber-700 mt-0.5">{note}</p>}
              </div>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
